import { api } from "./api";
import {
  CreateSupportArticle,
  CreateSupportCategory,
  CreateSupportContactReason,
  SupportArticleDetail,
  SupportArticleListItem,
  SupportArticlesQueryParams,
  SupportCategoriesQueryParams,
  SupportCategory,
  SupportConfig,
  SupportContactReason,
  SupportContactsQueryParams,
  SupportContactsResult,
  SupportFeedbackDashboardItem,
  ToggleSupportArticleFeatured,
  UpdateSupportArticle,
  UpdateSupportCategory,
  UpdateSupportConfig,
  UpdateSupportContactReason,
} from "../../models/support";
import {
  mapSupportArticle,
  mapSupportArticleDetail,
  mapSupportCategory,
  mapSupportConfig,
  mapSupportContactReason,
  mapSupportContactsResult,
  mapSupportFeedbackDashboardItem,
} from "../../utils/mappers";

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  meta?: { warning?: string };
}

const unwrap = <T>(response: ApiEnvelope<T>): T => response.data;

export const supportApi = api.injectEndpoints({
  endpoints: (build) => ({
    getSupportCategories: build.query<
      SupportCategory[],
      SupportCategoriesQueryParams | void
    >({
      query: (params) => ({
        url: "/bo/support/categories",
        params:
          params?.isActive !== undefined
            ? { is_active: String(params.isActive) }
            : undefined,
      }),
      transformResponse: (r: ApiEnvelope<any[]>) => unwrap(r).map(mapSupportCategory),
      providesTags: ["supportCategories"],
    }),
    createSupportCategory: build.mutation<SupportCategory, CreateSupportCategory>({
      query: (body) => ({ url: "/bo/support/categories", method: "POST", body }),
      transformResponse: (r: ApiEnvelope<any>) => mapSupportCategory(unwrap(r)),
      invalidatesTags: ["supportCategories"],
    }),
    updateSupportCategory: build.mutation<SupportCategory, UpdateSupportCategory>({
      query: ({ id, ...body }) => ({
        url: `/bo/support/categories/${id}`,
        method: "PUT",
        body,
      }),
      transformResponse: (r: ApiEnvelope<any>) => mapSupportCategory(unwrap(r)),
      invalidatesTags: ["supportCategories", "supportArticles"],
    }),
    reorderSupportCategories: build.mutation<void, { category_ids: string[] }>({
      query: (body) => ({ url: "/bo/support/categories/reorder", method: "PUT", body }),
      invalidatesTags: ["supportCategories"],
    }),
    deleteSupportCategory: build.mutation<{ deleted_articles: number }, string>({
      query: (id) => ({
        url: `/bo/support/categories/${id}`,
        method: "DELETE",
        params: { confirm: "true" },
      }),
      transformResponse: (r: ApiEnvelope<{ deleted_articles: number }>) => unwrap(r),
      invalidatesTags: ["supportCategories", "supportArticles", "supportFeedback"],
    }),

    getSupportArticles: build.query<SupportArticleListItem[], SupportArticlesQueryParams | void>({
      query: (params) => ({
        url: "/bo/support/articles",
        params: params
          ? {
              ...(params.categoryId && { category_id: params.categoryId }),
              ...(params.isFeatured !== undefined && { is_featured: String(params.isFeatured) }),
              ...(params.isActive !== undefined && { is_active: String(params.isActive) }),
            }
          : undefined,
      }),
      transformResponse: (r: ApiEnvelope<any[]>) => unwrap(r).map(mapSupportArticle),
      providesTags: ["supportArticles"],
    }),
    getSupportArticleById: build.query<SupportArticleDetail, string>({
      query: (id) => ({ url: `/bo/support/articles/${id}` }),
      transformResponse: (r: ApiEnvelope<any>) => mapSupportArticleDetail(unwrap(r)),
      providesTags: (result, error, id) => [{ type: "supportArticles", id }],
    }),
    createSupportArticle: build.mutation<SupportArticleListItem, CreateSupportArticle>({
      query: (body) => ({ url: "/bo/support/articles", method: "POST", body }),
      transformResponse: (r: ApiEnvelope<any>) => mapSupportArticle(unwrap(r)),
      invalidatesTags: ["supportArticles", "supportCategories", "supportFeedback"],
    }),
    updateSupportArticle: build.mutation<SupportArticleListItem, UpdateSupportArticle>({
      query: ({ id, ...body }) => ({
        url: `/bo/support/articles/${id}`,
        method: "PUT",
        body,
      }),
      transformResponse: (r: ApiEnvelope<any>) => mapSupportArticle(unwrap(r)),
      invalidatesTags: (result, error, { id }) => [
        "supportArticles",
        "supportCategories",
        "supportFeedback",
        { type: "supportArticles", id },
      ],
    }),
    deleteSupportArticle: build.mutation<void, string>({
      query: (id) => ({ url: `/bo/support/articles/${id}`, method: "DELETE" }),
      invalidatesTags: ["supportArticles", "supportCategories", "supportFeedback"],
    }),
    toggleSupportArticleFeatured: build.mutation<void, ToggleSupportArticleFeatured>({
      query: ({ id, ...body }) => ({
        url: `/bo/support/articles/${id}/toggle-featured`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["supportArticles"],
    }),
    reorderFeaturedSupportArticles: build.mutation<
      void,
      { article_ids: string[]; tenant_id?: string | null }
    >({
      query: (body) => ({
        url: "/bo/support/articles/featured/reorder",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["supportArticles"],
    }),

    getSupportContactReasons: build.query<SupportContactReason[], void>({
      query: () => ({ url: "/bo/support/contact-reasons" }),
      transformResponse: (r: ApiEnvelope<any[]>) => unwrap(r).map(mapSupportContactReason),
      providesTags: ["supportReasons"],
    }),
    createSupportContactReason: build.mutation<SupportContactReason, CreateSupportContactReason>({
      query: (body) => ({ url: "/bo/support/contact-reasons", method: "POST", body }),
      transformResponse: (r: ApiEnvelope<any>) => mapSupportContactReason(unwrap(r)),
      invalidatesTags: ["supportReasons"],
    }),
    updateSupportContactReason: build.mutation<SupportContactReason, UpdateSupportContactReason>({
      query: ({ id, ...body }) => ({
        url: `/bo/support/contact-reasons/${id}`,
        method: "PUT",
        body,
      }),
      transformResponse: (r: ApiEnvelope<any>) => mapSupportContactReason(unwrap(r)),
      invalidatesTags: ["supportReasons"],
    }),
    reorderSupportContactReasons: build.mutation<void, { reason_ids: string[] }>({
      query: (body) => ({
        url: "/bo/support/contact-reasons/reorder",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["supportReasons"],
    }),
    deleteSupportContactReason: build.mutation<void, string>({
      query: (id) => ({ url: `/bo/support/contact-reasons/${id}`, method: "DELETE" }),
      invalidatesTags: ["supportReasons"],
    }),

    getSupportConfig: build.query<SupportConfig, void>({
      query: () => ({ url: "/bo/support/config" }),
      transformResponse: (r: ApiEnvelope<any>) => mapSupportConfig(unwrap(r)),
      providesTags: ["supportConfig"],
    }),
    updateSupportConfig: build.mutation<
      { config: SupportConfig; warning?: string },
      UpdateSupportConfig
    >({
      query: (body) => ({ url: "/bo/support/config", method: "PUT", body }),
      transformResponse: (r: ApiEnvelope<any>) => ({
        config: mapSupportConfig(unwrap(r)),
        warning: r.meta?.warning,
      }),
      invalidatesTags: ["supportConfig", "supportArticles"],
    }),

    getSupportContacts: build.query<SupportContactsResult, SupportContactsQueryParams | void>({
      query: (params) => ({
        url: "/bo/support/contacts",
        params: params
          ? {
              ...(params.page && { page: params.page }),
              ...(params.limit && { limit: params.limit }),
              ...(params.reasonId && { reason_id: params.reasonId }),
              ...(params.isRead !== undefined && { is_read: String(params.isRead) }),
              ...(params.dateFrom && { date_from: params.dateFrom }),
              ...(params.dateTo && { date_to: params.dateTo }),
            }
          : undefined,
      }),
      transformResponse: (r: ApiEnvelope<any>) => mapSupportContactsResult(unwrap(r)),
      providesTags: ["supportContacts"],
    }),
    markSupportContactRead: build.mutation<void, string>({
      query: (id) => ({ url: `/bo/support/contacts/${id}/read`, method: "PUT" }),
      invalidatesTags: ["supportContacts"],
    }),

    getSupportFeedbackDashboard: build.query<
      SupportFeedbackDashboardItem[],
      { categoryId?: string } | void
    >({
      query: (params) => ({
        url: "/bo/support/feedback/dashboard",
        params: params?.categoryId ? { category_id: params.categoryId } : undefined,
      }),
      transformResponse: (r: ApiEnvelope<any[]>) => unwrap(r).map(mapSupportFeedbackDashboardItem),
      providesTags: ["supportFeedback"],
    }),
  }),
});

export const {
  useGetSupportCategoriesQuery,
  useCreateSupportCategoryMutation,
  useUpdateSupportCategoryMutation,
  useReorderSupportCategoriesMutation,
  useDeleteSupportCategoryMutation,
  useGetSupportArticlesQuery,
  useGetSupportArticleByIdQuery,
  useCreateSupportArticleMutation,
  useUpdateSupportArticleMutation,
  useDeleteSupportArticleMutation,
  useToggleSupportArticleFeaturedMutation,
  useReorderFeaturedSupportArticlesMutation,
  useGetSupportContactReasonsQuery,
  useCreateSupportContactReasonMutation,
  useUpdateSupportContactReasonMutation,
  useReorderSupportContactReasonsMutation,
  useDeleteSupportContactReasonMutation,
  useGetSupportConfigQuery,
  useUpdateSupportConfigMutation,
  useGetSupportContactsQuery,
  useMarkSupportContactReadMutation,
  useGetSupportFeedbackDashboardQuery,
} = supportApi;
